
import { computed, defineComponent, onMounted, PropType, reactive, ref } from 'vue'

import Loader from '@/common/components/UI/Loader/index.vue'
import OkInput from '@/modules/OktellModule/components/OkInput/index.vue'
import OkSelect from '@/modules/OktellModule/components/OkSelect/index.vue'
import OkTextarea from '@/modules/OktellModule/components/OkTextarea/index.vue'
import OkCheckbox from '@/modules/OktellModule/components/OkCheckbox/index.vue'
import OkButton from '@/modules/OktellModule/components/OkButton/index.vue'
import OkRadio from '@/modules/OktellModule/components/OkRadio/index.vue'
import OkFile from '@/modules/OktellModule/components/OkFile/index.vue'

import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'
import { PRICE_MASK } from '@/core/constants/masks.constants'
import {
    LTL_SHIPMENT_TYPES,
    PAYMENT_METHOD_OPTIONS,
    PAYMENT_TYPE_OPTIONS,
} from '@/core/constants/create_order.constants'

import { DataState } from '@/core/classes/DataState.class'

import { maxLength, requiredIf, required, decimal, integer } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

import { UpdateInvoicePayloadT, UplodaFilesResponseT } from '@/core/types/invoices.types'
import { AdditionalServicesI } from '@/core/types/additional_services.types'
import { PayerT } from '@/core/types/companies.types'
import { CompletedCargoT, TemperatureRegimeT, UploadedFileT } from '@/core/types/create_order.types'
import { InvoiceT } from '@/core/types/orders.types'

import { companies } from '@/core/realisations/companies.realisation'
import { orders } from '@/core/realisations/orders.realisation'
import { invoices } from '@/core/realisations/invoices.realisation'

import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'
import { useMaska } from '@/common/composable/useMaska'
import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    components: {
        Loader,
        OkInput,
        OkSelect,
        OkTextarea,
        OkCheckbox,
        OkRadio,
        OkButton,
        OkFile,
    },
    props: {
        invoice: {
            type: Object as PropType<InvoiceT>,
            default: null,
        },
        //
        invoiceId: {
            type: Number,
            default: null,
        },
        updatingCargo: {
            type: Object as PropType<CompletedCargoT>,
            default: null,
        },
    },
    emits: ['submit', 'cancel-update'],
    setup(props, { emit }) {
        const account = useAccount()
        const { unmaskNumbers } = useMaska()
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        let additional_services: any = reactive(EMPTY_ADDITIONAL_SERVICES)

        const state = reactive({
            product_name: null as string | null,
            customer_invoice_number: null as string | null,
            weight: null as number | null,
            places: null as number | null,
            depth: null as number | null,
            width: null as number | null,
            height: null as number | null,
            //
            volume: null as number | null,
            //
            annotation: null as string | null,
            is_return_documents: 0,
            is_ensure: 0,
            is_dangerous: 0,
            is_temperature_regime_active: false,
            temperature_regime_type_id: null as number | null,
            invoice_files: [] as string[],
            certificate_of_safety_files: [] as string[],
            verify: false,
            //
            payer_company_id: null as number | null,
            cod_payment: null as string | null,
            cargo_name: null as string | null,
            declared_price: null as string | null,
            shipment_type: 1,
            payment_method: PAYMENT_METHOD_OPTIONS[0].id,
            payment_type: 1,
            cash_sum: null as string | null,
            //
            is_admin: false,
        })

        const files_state = reactive({
            is_loading: false,
            //
            invoice_files: [] as File[],
            certificate_of_safety_files: [] as File[],
        })

        const files_backend_state = reactive({
            invoice_files: [] as UploadedFileT[],
            certificate_of_safety_files: [] as UploadedFileT[],
        })

        const data_state = reactive({
            payers: new DataState<PayerT>({}),
            temperature_regimes: new DataState<TemperatureRegimeT>({}),
            payment_methods: PAYMENT_METHOD_OPTIONS,
            verify: {
                type: 0 as number,
                changed_by_user: false,
                is_loading: false,
                is_failed: false,
            },
        })

        const CargoValidation = {
            product_name: { required },
            customer_invoice_number: {
                required: requiredIf(() => data_state.verify.type === 2 && state.verify),
                maxLength: maxLength(30),
            },
            weight: { required, decimal },
            places: { required, integer },
            depth: { required, decimal },
            width: { required, decimal },
            height: { required, decimal },
            payer_company_id: {
                required: requiredIf(
                    () =>
                        (state.payment_type === 2 && state.payment_method === 3) ||
                        state.payment_type === 3
                ),
            },
            cash_sum: { decimal },
            temperature_regime_type_id: {
                required: requiredIf(() => state.is_temperature_regime_active),
            },
            cargo_name: { required: requiredIf(() => Boolean(state.is_ensure)) },
        }
        const FilesValidation = {
            invoice_files: {
                required: requiredIf(
                    () => Boolean(state.is_dangerous) && !files_backend_state.invoice_files.length
                ),
            },
            certificate_of_safety_files: {
                required: requiredIf(
                    () =>
                        Boolean(state.is_dangerous) &&
                        !files_backend_state.certificate_of_safety_files.length
                ),
            },
        }

        const state_form$ = useVuelidate(CargoValidation, state)
        const files_state_form$ = useVuelidate(FilesValidation, files_state)

        const is_loading = ref(false)

        const resetFiles = () => {
            state.invoice_files = []
            state.certificate_of_safety_files = []

            files_state.invoice_files = []
            files_state.certificate_of_safety_files = []
        }

        const resetForm = () => {
            additional_services.hasCar = false
            additional_services.hasSoftPackage = false
            additional_services.hasManipulator = false
            additional_services.hasCrane = false
            additional_services.hasHydraulicTrolley = false
            additional_services.hasGrid = false
            additional_services.hasLoader = false
            additional_services.hasPallet = false

            state.product_name = null
            state.customer_invoice_number = null
            state.weight = null
            state.places = null
            state.depth = null
            state.width = null
            state.height = null
            state.annotation = null
            state.is_return_documents = 0
            state.cod_payment = null
            state.cargo_name = null
            state.declared_price = null
            state.shipment_type = 1
            state.payment_type = 1
            state.payer_company_id = null
            state.cash_sum = null
            data_state.payment_methods =
                state.payment_type === 3
                    ? PAYMENT_METHOD_OPTIONS.filter((p) => p.id === 3)
                    : PAYMENT_METHOD_OPTIONS
            state.payment_method = PAYMENT_METHOD_OPTIONS[0].id

            state.is_dangerous = 0
            state.is_temperature_regime_active = false
            state.temperature_regime_type_id = null
            resetFiles()

            state_form$.value.$reset()
            files_state_form$.value.$reset()
        }

        const uploadFiles = async () => {
            try {
                is_loading.value = true

                let invoice_files: UplodaFilesResponseT = { data: [] }
                if (files_state.invoice_files.length) {
                    invoice_files = await invoices.uploadFiles(
                        getFormData({ type: 17, files: files_state.invoice_files })
                    )
                }

                let certificate_of_safety_files: UplodaFilesResponseT = { data: [] }
                if (files_state.certificate_of_safety_files.length) {
                    certificate_of_safety_files = await invoices.uploadFiles(
                        getFormData({ type: 18, files: files_state.certificate_of_safety_files })
                    )
                }

                state.invoice_files = invoice_files.data
                state.certificate_of_safety_files = certificate_of_safety_files.data
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При загрузке файлов произошла ошбика',
                })
            } finally {
                is_loading.value = false
            }
        }

        const emitSubmit = async () => {
            if (is_loading.value) {
                return
            }

            state_form$.value.$touch()
            files_state_form$.value.$touch()

            if (state_form$.value.$invalid || files_state_form$.value.$invalid) {
                return
            }

            if (!Number(state.width) || !Number(state.height) || !Number(state.depth)) {
                return
            }

            const volume_weight =
                (Number(state.width) * Number(state.height) * Number(state.depth)) / 1000000
            state.volume = volume_weight > 0.01 ? Number(volume_weight.toFixed(2)) : 0.01

            // Если выключено и не трогали то null
            // Если включено и отказались 0
            // Если включено и приняли type
            let verify = null

            if (data_state.verify.type > 0) {
                if (state.verify) {
                    verify = data_state.verify.type
                } else {
                    verify = 0
                }
            }

            // Загрузить файлы, чтобы сохранить и получить их в формате string[]
            if (
                state.is_dangerous &&
                (files_state.invoice_files.length || files_state.certificate_of_safety_files.length)
            )
                await uploadFiles()

            if (
                props.invoice &&
                props.invoice.take_date &&
                props.invoice.period_id &&
                props.invoiceId
            ) {
                try {
                    is_loading.value = true
                    const { unmaskNumbers } = useMaska()

                    const payload: UpdateInvoicePayloadT = {
                        additional_service: additional_services as AdditionalServicesI,
                        product_name: state.product_name,
                        dop_invoice_number: state.customer_invoice_number,
                        annotation: state.annotation,
                        cod_payment: state.cod_payment
                            ? Number(unmaskNumbers(state.cod_payment))
                            : 0,
                        declared_price: state.declared_price
                            ? Number(unmaskNumbers(state.declared_price))
                            : 0,
                        take_date: props.invoice.take_date,
                        period_id: props.invoice.period_id,
                        places: Number(state.places),
                        weight: Number(state.weight),
                        width: Number(state.width),
                        height: Number(state.height),
                        depth: Number(state.depth),
                        volume: state.volume,
                        cargo_name: state.cargo_name,
                        should_return_document: state.is_return_documents,
                        shipment_type: state.shipment_type,
                        payment_type: state.payment_type,
                        payment_method: state.payment_method,
                        verify,
                        //
                        is_dangerous: state.is_dangerous,
                        temperature_regime_type_id: state.temperature_regime_type_id,
                        invoice_files: state.invoice_files,
                        certificate_of_safety_files: state.certificate_of_safety_files,
                    }

                    if (state.payer_company_id) payload.payer_company_id = state.payer_company_id
                    if (state.cash_sum) payload.cash_sum = Number(state.cash_sum)

                    await invoices.updateInvoice(props.invoiceId, payload)

                    notifications.notify({
                        title: 'Накладная отредактирована',
                        type: 'success',
                    })

                    emit('submit', {
                        ...state,
                        temperature_regime_type_id: state.temperature_regime_type_id,
                        weight: Number(state.weight),
                        places: Number(state.places),
                        depth: Number(state.depth),
                        width: Number(state.width),
                        height: Number(state.height),
                        payer_company_id: state.payer_company_id,
                        cash_sum: Number(state.cash_sum),
                        additional_services: { ...additional_services },
                        verify,
                        invoice_files_fe: files_state.invoice_files,
                        certificate_of_safety_files_fe: files_state.certificate_of_safety_files,
                    })

                    resetForm()
                } catch (error) {
                    console.error(error)
                    notifications.notifyError({
                        error,
                        title: 'При редактировании накладной произошла ошбика',
                    })
                } finally {
                    is_loading.value = false
                }

                return
            }

            emit('submit', {
                ...state,
                temperature_regime_type_id: state.temperature_regime_type_id,
                weight: Number(state.weight),
                places: Number(state.places),
                depth: Number(state.depth),
                width: Number(state.width),
                height: Number(state.height),
                payer_company_id: state.payer_company_id,
                cash_sum: state.cash_sum ? unmaskNumbers(state.cash_sum) : '',
                additional_services: { ...additional_services },
                verify,
                invoice_files_fe: files_state.invoice_files,
                certificate_of_safety_files_fe: files_state.certificate_of_safety_files,
            })

            resetForm()
        }

        const onIsDangerousChange = () => {
            if (state.is_dangerous === 0) resetFiles()
        }
        const onTemperatureRegimeChanged = () => {
            if (!state.is_temperature_regime_active) state.temperature_regime_type_id = null
        }

        const triggerGrid = () => (additional_services.hasGrid = !additional_services.hasGrid)
        const triggerSoftPackage = () =>
            (additional_services.hasSoftPackage = !additional_services.hasSoftPackage)
        const triggerPallet = () => (additional_services.hasPallet = !additional_services.hasPallet)
        const triggerReturn = () => (state.is_return_documents = Number(!state.is_return_documents))
        const triggerEnsure = () => (state.is_ensure = Number(!state.is_ensure))
        const triggerDanger = () => {
            state.is_dangerous = Number(!state.is_dangerous)
            onIsDangerousChange()
        }
        const triggerTemperature = () => {
            state.is_temperature_regime_active = !state.is_temperature_regime_active
            onTemperatureRegimeChanged()
        }

        const loadPayers = async () => {
            if (!props.invoice || !props.invoice.company_id) return

            try {
                data_state.payers.is_loading = true
                const response = await companies.getPayers(1, props.invoice.company_id)
                data_state.payers.list = response.data

                data_state.payers.is_failed = false
                data_state.payers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.payers.failure.catchError(error)

                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_failed = true
                }
            } finally {
                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_loading = false
                }
            }
        }

        const loadTemperatureRegimes = async () => {
            try {
                data_state.temperature_regimes.is_loading = true
                const response = await orders.getTemperatureRegimes()
                data_state.temperature_regimes.list = response.data

                data_state.temperature_regimes.is_failed = false
                data_state.temperature_regimes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.temperature_regimes.failure.catchError(error)

                if (!data_state.temperature_regimes.failure.error_state.is_cancel) {
                    data_state.temperature_regimes.is_failed = true
                }
            } finally {
                if (!data_state.temperature_regimes.failure.error_state.is_cancel) {
                    data_state.temperature_regimes.is_loading = false
                }
            }
        }

        const onUpdateAnnotation = (annotation: string | null) => (state.annotation = annotation)

        const setAdditionalServices = () => {
            const additional_services_values: any =
                props.updatingCargo.logistics_info.additional_services

            for (const service_name in additional_services_values) {
                additional_services[service_name] = additional_services_values[service_name]
            }
        }

        const setCargo = () => {
            const cargo = props.updatingCargo.logistics_info

            setAdditionalServices()

            state.product_name = cargo.product_name
            state.customer_invoice_number = cargo.customer_invoice_number
            state.weight = cargo.weight
            state.places = cargo.places
            state.depth = cargo.depth
            state.width = cargo.width
            state.height = cargo.height
            state.annotation = cargo.annotation
            state.is_return_documents = cargo.is_return_documents
            state.cod_payment = cargo.cod_payment
            state.cargo_name = cargo.cargo_name
            state.declared_price = cargo.declared_price

            if (Number(state.declared_price)) {
                state.is_ensure = 1
            }

            state.shipment_type = cargo.shipment_type
            state.payer_company_id = cargo.payer_company_id
            state.cash_sum = cargo.cash_sum?.toString() || null
            state.payment_method = cargo.payment_method

            state.is_dangerous = cargo.is_dangerous
            state.is_temperature_regime_active = Boolean(cargo.temperature_regime_type_id)
            state.temperature_regime_type_id = cargo.temperature_regime_type_id
            files_backend_state.invoice_files = cargo.invoice_files as UploadedFileT[]
            files_backend_state.certificate_of_safety_files =
                cargo.certificate_of_safety_files as UploadedFileT[]

            state.payment_type = cargo.payment_type
            onPaymentTypeChange()
        }

        const deleteFile = async ({ id, index }: { id: number; index: number }, type: string) => {
            try {
                files_state.is_loading = true
                await invoices.deleteFile(id)

                if (type === 'invoice') {
                    files_backend_state.invoice_files.splice(index, 1)
                } else if (type === 'certificate') {
                    files_backend_state.certificate_of_safety_files.splice(index, 1)
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При удалении файла произошла ошибка',
                })
            } finally {
                files_state.is_loading = false
            }
        }

        const onSelectType = (id: number) => (state.shipment_type = id)
        const isTypeActive = (id: number) => state.shipment_type === id

        const onPaymentTypeChange = () => {
            if (state.payment_type === 3) {
                loadPayers()
                return
            }

            if (state.payment_type === 2) {
                loadPayers()
                data_state.payment_methods = PAYMENT_METHOD_OPTIONS
                return
            }

            data_state.payment_methods = PAYMENT_METHOD_OPTIONS
            state.payer_company_id = null
        }

        const onPaymentMethodChange = () => {
            if (state.payment_method === 3) state.cash_sum = null
        }

        const isOnPayerSelect = computed(
            () =>
                (state.payment_type === 2 && state.payment_method === 3) || state.payment_type === 3
        )

        const isOnCashInput = computed(
            () => state.payment_method === 2 || state.payment_method === 4
        )

        const onPayerSelect = (payer: number | null) => {
            if (!payer) return
            // update payment methods
        }

        //

        const customInvoiceNumberPlaceholder = computed(() => {
            if (data_state.verify.type === 2 && state.verify) return 'Номер накладной Kaspi'
            return 'Номер накладной заказчика'
        })

        const ltlShipmentTypes = computed(() => {
            if (
                props.invoice &&
                props.invoice.receiver &&
                props.invoice.receiver.city_id === props.invoice.sender.city_id
            ) {
                return LTL_SHIPMENT_TYPES.filter((type) => type.id !== 2)
            }

            return LTL_SHIPMENT_TYPES
        })

        const isInvoiceFiles = computed(() => {
            return files_backend_state.invoice_files.length
        })

        const isCertificate = computed(() => {
            return files_backend_state.certificate_of_safety_files.length
        })

        const getPaymentMethods = computed(() => {
            return state.payment_type === 3
                ? data_state.payment_methods.filter((p) => p.id === 3)
                : data_state.payment_methods
        })

        //

        onMounted(async () => {
            state.is_admin = account.haveRole(11)
            loadTemperatureRegimes()
            setCargo()
        })

        //

        return {
            LTL_SHIPMENT_TYPES,
            PAYMENT_TYPE_OPTIONS,
            additional_services,
            state,
            files_state,
            files_backend_state,
            data_state,
            //
            state_form$,
            files_state_form$,
            //
            emitSubmit,
            PRICE_MASK,
            is_loading,
            loadPayers,
            loadTemperatureRegimes,
            isOnPayerSelect,
            isOnCashInput,
            onPaymentTypeChange,
            onPaymentMethodChange,
            onPayerSelect,
            onTemperatureRegimeChanged,
            deleteFile,
            onIsDangerousChange,
            onUpdateAnnotation,
            //
            triggerGrid,
            triggerSoftPackage,
            triggerPallet,
            triggerReturn,
            triggerEnsure,
            triggerDanger,
            triggerTemperature,
            onSelectType,
            isTypeActive,
            //
            customInvoiceNumberPlaceholder,
            ltlShipmentTypes,
            isInvoiceFiles,
            isCertificate,
            getPaymentMethods,
        }
    },
})
